/**
 * Voyado Elevate: member price overlay.
 *
 * Elevate's catalog cannot carry `custom.memberprice` — it is a variant
 * metafield, and the Shopify app only maps product-level metafields (see the
 * comment above isMember() in elevate-core.js). `prices` (Elevate's
 * `customPrices`) stays empty for the same reason and there is no dashboard
 * path around it.
 *
 * Rather than wait on Voyado, this fetches the metafield straight from
 * Shopify's own Storefront API. Batched by product, not SKU: the Storefront
 * API's `products(query:)` and `search(query:)` both silently ignore a
 * `sku:` filter (confirmed empirically — no supported way to look up a
 * variant by SKU exists there), so this uses `nodes(ids:)` instead, keyed on
 * `gid://shopify/Product/{key}`. Elevate's product `key` is that same
 * numeric Shopify product ID — confirmed against the connector's own
 * generated catalog import payload, not just inferred.
 *
 * A fetched product's variants are then matched back to a card by SKU:
 * Elevate's variant `key` matches CG's Shopify SKU in practice — confirmed
 * against a live product ("9207019" for Christiania 1739 Kitchenline
 * Skjærefjøl Sett 4stk med Stativ) — even though the official schema only
 * documents `key` as a generic identifier, not a SKU field. If a future
 * catalog sync ever changes what `key` holds, this whole file stops finding
 * matches and fails quiet (no member price shown), not wrong.
 *
 * Deliberately a second pass over already-rendered cards, not folded into
 * elevate-card.js's render(): the Storefront API call is async, and render()
 * is called synchronously from places (autocomplete, recommendation
 * carousels) that would all need to become async for no benefit to them.
 *
 * Requires:
 * - The Member Price metafield definition to have "Storefront API access"
 *   enabled (confirmed on custom.memberprice).
 * - A Storefront API access token set in Settings → Voyado Elevate, exposed
 *   by snippets/elevate-member-price-config.liquid as
 *   window.CGElevateMemberPriceConfig. Without one, enhance() is a no-op.
 *
 * Mirrors the business rule in snippets/price.liquid: member price only
 * replaces the regular price when the product is not already on sale.
 *
 * Exposed as `window.CGElevateMemberPrice`. Requires elevate-core.js.
 */

(function () {
  'use strict';

  const E = window.CGElevate;
  // Matches both the grid card (elevate-card.js, .elevate-card) and the
  // autocomplete product item (elevate-autocomplete.js, .elevate-ac__product)
  // — anything carrying both data attributes, regardless of which renderer
  // produced it.
  const CARD_SELECTOR = '[data-product-key][data-variant-key]';
  const DONE_CLASS = 'elevate-card--member-price-checked';
  const API_VERSION = '2025-01';

  function config() {
    return window.CGElevateMemberPriceConfig || {};
  }

  const QUERY =
    'query MemberPrices($ids: [ID!]!) {' +
    '  nodes(ids: $ids) {' +
    '    ... on Product {' +
    '      variants(first: 50) {' +
    '        edges {' +
    '          node {' +
    '            sku' +
    '            memberPrice: metafield(namespace: "custom", key: "memberprice") {' +
    '              value' +
    '            }' +
    '          }' +
    '        }' +
    '      }' +
    '    }' +
    '  }' +
    '}';

  function buildProductGids(productKeys) {
    return productKeys.map((key) => 'gid://shopify/Product/' + key);
  }

  function graphql(variables) {
    const shopDomain = config().shopDomain;
    const token = config().storefrontAccessToken;
    if (!shopDomain || !token) return Promise.resolve(null);

    return fetch('https://' + shopDomain + '/api/' + API_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query: QUERY, variables: variables })
    })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
  }

  /**
   * Returns a `{sku: number}` map, keyed by variant SKU so the card-matching
   * logic in enhance() does not need to know about product keys at all.
   * SKUs with no member price are omitted.
   */
  function fetchMemberPrices(productKeys) {
    if (!productKeys.length) return Promise.resolve({});

    return graphql({ ids: buildProductGids(productKeys) }).then((json) => {
      const map = {};
      const nodes = (json && json.data && json.data.nodes) || [];

      nodes.forEach((node) => {
        // null when a product key from Elevate no longer resolves to a
        // Shopify product (deleted/unpublished since the last catalog sync).
        if (!node) return;
        const variantEdges = (node.variants && node.variants.edges) || [];
        variantEdges.forEach((variantEdge) => {
          const variant = variantEdge.node;
          const raw = variant.memberPrice && variant.memberPrice.value;
          if (!variant.sku || raw === undefined || raw === null || raw === '') return;
          const value = Number(raw);
          if (value > 0) map[variant.sku] = value;
        });
      });

      return map;
    });
  }

  /**
   * Two surfaces render a price with different markup, so each needs its own
   * way of replacing it: the grid card's `.elevate-card__price` is a wrapper
   * div (safe to overwrite with innerHTML), while the autocomplete panel's
   * `.elevate-ac__price` is the price text itself with a `<s>` sibling for
   * the "was" price, not a wrapper.
   */
  const PRICE_SHAPES = [
    {
      selector: '.elevate-card__price',
      saleClass: 'elevate-card__price--sale',
      memberClass: 'elevate-card__price--member',
      badgeContainer: '.elevate-card__badges',
      badgeParent: '.elevate-card__media',
      badgeClass: 'badge badge--member-price',
      apply: (priceEl, value) => {
        const previousPriceText = priceEl.textContent.trim();
        priceEl.innerHTML =
          '<span class="elevate-card__price-current price-item--member">' + E.esc(E.money(value)) + '</span>' +
          '<s class="elevate-card__price-was price-item--regular">' + E.esc(previousPriceText) + '</s>';
      }
    },
    {
      selector: '.elevate-ac__price',
      // The panel's card is a structural copy of product-card.liquid (see
      // productItem() in elevate-autocomplete.js), so the sale marker is the
      // theme's own price-item--sale rather than a class of its own.
      saleClass: 'price-item--sale',
      memberClass: 'price-item--member',
      // Same place the native card puts its member badge - over the image, in
      // .card__badge - so the badge does not move when the card is upgraded.
      badgeContainer: '.card__badge',
      badgeParent: '.card__inner',
      badgeClass: 'badge elevate-ac__badge--member-price',
      apply: (priceEl, value) => {
        const previousPriceText = priceEl.textContent.trim();
        priceEl.textContent = E.money(value);
        // A whole <dd class="price__compare">, appended after the amount's own
        // <dd>, not an <s> beside the amount: that is the shape price.liquid's
        // member branch produces, and component-price.css sizes the struck-out
        // price through .price__compare.
        const amountCell = priceEl.closest('dd') || priceEl;
        amountCell.insertAdjacentHTML(
          'afterend',
          '<dd class="price__compare">' +
            '<s class="price-item price-item--regular elevate-ac__price-was">' +
            E.esc(previousPriceText) +
            '</s></dd>'
        );
      }
    }
  ];

  const MEMBER_PRICE_BADGE_CLASS = 'elevate-member-price-badge';

  /**
   * A "Medlemstilbud" badge, distinct from the tag-driven "CG Medlem" one in
   * assets/elevate-core.js's isMember() - this one is driven purely by member
   * price actually being applied, so it never disagrees with the price shown
   * right next to it. Independent of that other badge; a card can carry
   * either, both, or neither.
   *
   * Both surfaces keep their badges in a container over the image, created
   * here when the renderer found nothing to put in it: `.elevate-card__badges`
   * inside `.elevate-card__media` for the grid card, and the theme's own
   * `.card__badge` inside `.card__inner` for the autocomplete panel, whose
   * card copies product-card.liquid's structure.
   */
  function addMemberPriceBadge(card, shape) {
    if (card.querySelector('.' + MEMBER_PRICE_BADGE_CLASS)) return;

    let container = card.querySelector(shape.badgeContainer);
    if (!container && shape.badgeParent) {
      const parent = card.querySelector(shape.badgeParent);
      if (!parent) return;
      container = document.createElement('div');
      container.className = shape.badgeContainer.slice(1);
      parent.prepend(container);
    }
    if (!container) return;

    container.insertAdjacentHTML(
      'beforeend',
      '<span class="' + shape.badgeClass + ' ' + MEMBER_PRICE_BADGE_CLASS + '" aria-hidden="true">' +
        E.esc(E.t('member_price_badge', 'Member offer')) +
        '</span>'
    );
  }

  /**
   * Replaces the regular/sale price markup with the member price, crossing
   * out what was there before — same rule as the member branch of
   * snippets/price.liquid: a discounted product keeps its sale price, it
   * does not also get a member price on top.
   */
  function applyMemberPrice(card, value) {
    for (let i = 0; i < PRICE_SHAPES.length; i++) {
      const shape = PRICE_SHAPES[i];
      const priceEl = card.querySelector(shape.selector);
      if (!priceEl) continue;
      if (priceEl.classList.contains(shape.saleClass)) return;
      // memberClass may be more than one space-separated class name.
      priceEl.classList.add.apply(priceEl.classList, shape.memberClass.split(' '));
      shape.apply(priceEl, value);
      addMemberPriceBadge(card, shape);
      return;
    }
  }

  /**
   * Finds every rendered card carrying a product key, fetches member prices
   * for all of them in one Storefront API call, and patches the ones that
   * have one. Safe to call again after pagination/filtering appends more
   * cards: already-checked cards are skipped rather than re-fetched.
   *
   * Batches on product key, not variant key/SKU — see buildProductGids.
   * Matching a fetched price back to a card is still done by variant
   * key/SKU, since that is what a card actually carries and a product can
   * resolve to several variants.
   */
  function enhance(container) {
    if (!container) return Promise.resolve();

    const cards = Array.prototype.filter.call(
      container.querySelectorAll(CARD_SELECTOR),
      (card) => !card.classList.contains(DONE_CLASS)
    );
    if (!cards.length) return Promise.resolve();

    const productKeys = cards
      .map((card) => card.dataset.productKey)
      .filter(Boolean)
      .filter((key, index, all) => all.indexOf(key) === index);

    return fetchMemberPrices(productKeys).then((prices) => {
      cards.forEach((card) => {
        card.classList.add(DONE_CLASS);
        const value = prices[card.dataset.variantKey];
        if (value !== undefined) applyMemberPrice(card, value);
      });
    });
  }

  window.CGElevateMemberPrice = {
    enhance: enhance,
    fetchMemberPrices: fetchMemberPrices
  };
})();
